import React, { useState, useLayoutEffect, useEffect, useContext, useRef } from 'react';

// Material Ui
import { Box, Alert } from '@mui/material';
// Custom Ui
import { useSearchParams, useLocation, useNavigate } from 'react-router';
import { connect } from 'react-redux';
import LoginForm from '../../components/Auth/Login';
import { ResetPasswordRequest, ResetPasswordUpdate } from '../../components/Auth/ResetPassword';
import Logo from '../../components/UI/Logo';

import { sanitaizeUsername, validateUsername } from '../../utils/validation';

import useUser from '../../hooks/useUser';
import useLocalStorage from '../../hooks/useLocalStorage';

// Redux
import * as userActions from '../../store/actions/user';
import { SubscriptionContext } from '../../contexts/SubscriptionContext';
import { StreakContext } from '../../contexts/StreakContext';
import { StyledBox, StyledGrid, StyledImageSection, StyledLoginSection } from './Login.styles';
import CheckIcon from '@mui/icons-material/Check';

export function Login(props) {
	const navigate = useNavigate();
	const { setUserId } = useContext(StreakContext);
	const location = useLocation();
	const { updateUser } = useContext(SubscriptionContext);
	const [credentials, setCredentials] = useState(props.user);
	const { isAuthenticated } = useUser();
	const [searchParams] = useSearchParams();
	const [buttonLoading, setButtonLoading] = useState(false);
	const { removeLocalStorage } = useLocalStorage('userData');

	useLayoutEffect(() => {
		if (isAuthenticated) {
			navigate('/');
		}
	}, [isAuthenticated, navigate]);

	const email = searchParams.get('email');
	const code = searchParams.get('code');
	const type = searchParams.get('type');

	// clean up on component unmount
	useEffect(
		() => () => {
			setCredentials({});
		},
		[]
	);

	useEffect(() => {
		if (email && email !== '' && code && code !== '' && type && Number(type) === 0) {
			props.verifyEmail(email, code, type, navigate);
		}
	}, [email, code, type, props, navigate]);

	useEffect(() => {
		if (
			location.search &&
			location.search.split('?')[1]?.split('=')[0] === 'redirect' &&
			location.search.split('?')[1]?.split('=')[1] === '1'
		) {
			removeLocalStorage();
		}

		if (location.pathname === '/change-password') {
			removeLocalStorage();
		}
	}, [location, removeLocalStorage]);

	const onInputChange = (e) => {
		const { name, value } = e.target;
		setCredentials((prevState) => ({
			...prevState,
			[name]: value
		}));
	};

	const onRegisterClick = () => {
		props.updateUserError('');
		navigate('/register');
	};

	const onResetPasswordClick = () => {
		props.updateUserError('');
		navigate('/reset-password');
	};

	const handleLogin = () => {
		props.loginUser(credentials, navigate, updateUser, setUserId);
	};

	const handleGenerateResetPassword = () => {
		props.generateResetPassword(credentials, navigate);
	};

	const handleResetPassword = (email, code) => {
		const isResetPasswordForm = true;
		const payload = {
			email,
			code,
			password: credentials.password,
			confirmPassword: credentials.confirmPassword
		};
		if (validateFields(isResetPasswordForm)) {
			props.updateUserError(null);
			props.resetPassword(payload, navigate, isResetPasswordForm);
		}
	};

	const validateFields = (isResetPasswordForm) => {
		let sanitizedLastName;
		let sanitizedFirstName;

		if (!isResetPasswordForm) {
			if (!checkFieldsExist()) {
				return false;
			}

			const [firstName, lastName] = sanitaizeUsername(credentials.firstName, credentials.lastName);
			sanitizedFirstName = firstName;
			sanitizedLastName = lastName;
		}

		let isValidUsername;
		if (sanitizedFirstName && sanitizedLastName) {
			isValidUsername = validateUsername(sanitizedFirstName, sanitizedLastName);
		}

		if (!isValidUsername && !isResetPasswordForm) {
			props.updateUserError('Numele sau prenumele este invalid.');
			return false;
		}

		if (credentials.confirmPassword && credentials.confirmPassword !== credentials.password) {
			props.updateUserError('Parolele introduse nu sunt identice. Te rugam reincearca.');
			return false;
		}

		if (credentials.password && credentials.password.length < 8) {
			props.updateUserError('Parola introdusă nu este suficient de lungă.');
			return false;
		}
		return true;
	};

	const checkFieldsExist = () => {
		// check field exists
		if (!credentials.firstName) {
			const firstNameInput = document.getElementById('firstName');
			if (firstNameInput) firstNameInput.value = '';
			props.updateUserError('Prenumele introdus este invalid.');
			return false;
		}

		if (!credentials.lastName) {
			const lastNameInput = document.getElementById('lastName');
			if (lastNameInput) lastNameInput.value = '';
			props.updateUserError('Numele introdus este invalid.');
			return false;
		}

		if (!credentials.county) {
			const countyInput = document.getElementById('county');
			if (countyInput) countyInput.value = '';
			props.updateUserError('Judetul introdus nu este valid.');
			return false;
		}

		if (!credentials.institution) {
			const institutionInput = document.getElementById('institution');
			if (institutionInput) institutionInput.value = '';
			props.updateUserError('Institutia introdusa nu este valid.');
			return false;
		}

		if (!credentials.email) {
			const emailInput = document.getElementById('email');
			if (emailInput) emailInput.value = '';
			props.updateUserError('Email-ul introdus nu este valid.');
			return false;
		}
		return true;
	};

	return (
		<StyledGrid container>
			<StyledImageSection item xs={12} sm={12} md={7} isteacher={'false'}></StyledImageSection>
			<StyledLoginSection item xs={12} sm={12} md={5}>
				<Box
					display="flex"
					flexDirection="column"
					height="100%"
					alignItems="left"
					justifyContent="center"
				>
					<StyledBox>
						<Logo cancelRedirect={true} height="100px" />
						{props.statusMessage ? (
							<Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
								{props.statusMessage}
							</Alert>
						) : null}
						{location.pathname === '/login' ? (
							<LoginForm
								onInputChange={onInputChange}
								onResetPasswordClick={onResetPasswordClick}
								onRegisterClick={onRegisterClick}
								onLoginClick={handleLogin}
								error={props.error}
								updateUserStatus={props.updateUserStatus}
								updateError={props.updateUserError}
								isStatusMessage={!!props.statusMessage}
							/>
						) : location.pathname === '/reset-password' ? (
							<ResetPasswordRequest
								onInputChange={onInputChange}
								handleResetPassword={handleGenerateResetPassword}
								error={props.error}
								updateError={props.updateUserError}
								navigate={navigate}
								isStatusMessage={!!props.statusMessage}
							/>
						) : location.pathname === '/change-password' ? (
							<ResetPasswordUpdate
								onInputChange={onInputChange}
								handleResetPasswordUpdate={handleResetPassword}
								error={props.error}
								updateError={props.updateUserError}
								isStatusMessage={!!props.statusMessage}
							/>
						) : null}
					</StyledBox>
				</Box>
			</StyledLoginSection>
		</StyledGrid>
	);
}

const mapStateToProps = (state) => ({
	error: state.user.error,
	user: state.user.credentials,
	statusMessage: state.user.statusMessage
});

const mapDispatchToProps = {
	loginUser: userActions.loginUser,
	registerUser: userActions.registerUser,
	generateResetPassword: userActions.generateResetPassword,
	resetPassword: userActions.resetPassword,
	updateUserError: userActions.updateUserError,
	updateUserStatus: userActions.updateUserStatus,
	verifyEmail: userActions.verifyEmail
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
